import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {
  BringForwardIcon,
  BringToFrontIcon,
  Button,
  SendBackwardIcon,
  SendToBackIcon,
} from '@taopix/taopix-design-system';
import {AssetType, BoxType, BoxTypeShape} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type ArrangeProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

const Arrange = ({sectionKey, item}: ArrangeProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {pictureBoxOptions, textBoxOptions, shapeBoxOptions, stickerOptions} = experience.EDITOR;

  const [notAvailableMessage, setNotAvailableMessage] = useState<string>();
  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage
    ) {
      return pictureBoxOptions.arrange;
    }
    if (boxType === BoxType.Text) {
      return textBoxOptions.arrange;
    }
    if (
      boxType === BoxType.Shape &&
      [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(
        item?.shape
      )
    ) {
      return shapeBoxOptions.arrange;
    }
    if (AssetType.ScrapbookImage === item?.hiresfiletype) {
      return stickerOptions.arrange;
    } else {
      return false;
    }
  };

  useEffect(() => {
    // This is an advanced option, so check whether advanced editing is on
    const canShowAdvancedOptions = ui.state.canEditPageLayout;

    // If there is a selected item
    if (item && item.ref !== -1) {
      // Show or hide this section based on the relevant experience and advanced editing configuration
      setShowSection(isActiveInExperience(item) && canShowAdvancedOptions);
      // Enable or disable this section based on the item's protection
      setDisableSection((item.errors?.imageMissing as boolean) || ui.state.swapActive);
      // Set the message to 'not available' in case the section was hidden or disabled
      setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
    } else {
      // If no item is selected, hide the section button
      setShowSection(false);
      // And show the 'please select an item' message
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [
    item?.asset,
    item?.asset?.assetType,
    item?.type,
    item?.image,
    item?.shape,
    ui.state.swapActive,
  ]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'lg:flex-1',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id={'arrangePalette'}
      title={t('boxpalettes:str_TitleArrange')}
      buttonIcon={<BringToFrontIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      subSection={true}
      showButton={showSection}
      disabled={disableSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses}>
        {!showSection || disableSection ? (
          // If the palette button has been hidden or disabled (by the experience, box protection or nothing is selected),
          // show the relevant 'unavailable' message in the palette in case the palette was left open when its status changed
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          // Only show the sub options if the palette is allowed in the experience and the content is not protected
          <>
            <Button
              data-testid={'box-settings-arrange-forward-button'}
              className={'w-full'}
              label={t('*:str_LabelForward')}
              buttonStyle={ui.state.isSmallScreen ? 'standard' : 'tertiary'}
              boxless={ui.state.isSmallScreen}
              startIcon={<BringForwardIcon />}
              labelAlignment={'left'}
              size={'large'}
              onClick={() => {
                app.core.dispatch('SHAPE.CHANGE_ZINDEX', {action: 'forward'}).catch(error => {
                  console.error(error);
                });
              }}
            />
            <Button
              data-testid={'box-settings-arrange-front-button'}
              className={'w-full'}
              label={t('*:str_LabelToFront')}
              buttonStyle={ui.state.isSmallScreen ? 'standard' : 'tertiary'}
              boxless={ui.state.isSmallScreen}
              startIcon={<BringToFrontIcon />}
              labelAlignment={'left'}
              size={'large'}
              onClick={() => {
                app.core.dispatch('SHAPE.CHANGE_ZINDEX', {action: 'front'}).catch(error => {
                  console.error(error);
                });
              }}
            />
            <Button
              data-testid={'box-settings-arrange-backward-button'}
              className={'w-full'}
              label={t('*:str_LabelBackward')}
              buttonStyle={ui.state.isSmallScreen ? 'standard' : 'tertiary'}
              boxless={ui.state.isSmallScreen}
              startIcon={<SendBackwardIcon />}
              labelAlignment={'left'}
              size={'large'}
              onClick={() => {
                app.core.dispatch('SHAPE.CHANGE_ZINDEX', {action: 'backward'}).catch(error => {
                  console.error(error);
                });
              }}
            />
            <Button
              data-testid={'box-settings-arrange-back-button'}
              className={'w-full'}
              label={t('*:str_LabelToBack')}
              buttonStyle={ui.state.isSmallScreen ? 'standard' : 'tertiary'}
              boxless={ui.state.isSmallScreen}
              startIcon={<SendToBackIcon />}
              labelAlignment={'left'}
              size={'large'}
              onClick={() => {
                app.core.dispatch('SHAPE.CHANGE_ZINDEX', {action: 'back'}).catch(error => {
                  console.error(error);
                });
              }}
            />
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default Arrange;
